<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <BRow
      class="py-2"
    >
      <BCol
        cols="12"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterSearch"
            v-model="tableAreas.filter.search"
            placeholder="search here"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>
      <BCol
        cols="12"
      >
        <BButton
          size="sm"
          variant="primary"
          @click.prevent="createArea"
        >
          Create Area
        </BButton>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableAreas"
      :provider="tableProvider"
      :fields="tableAreas.fields"
      :filter="tableAreas.filter"
      :filter-on="tableAreas.filterOn"
      :sort-by="tableAreas.sortBy"
      :sort-desc="tableAreas.sortDesc"
      :sort-direction="tableAreas.sortDirection"
      :total-rows="tableAreas.totalRows"
      :current-page="tableAreas.currentPage"
      :per-page="tableAreas.perPage"
      :stacked="tableAreas.stacked"
      :busy="tableAreas.busy"
    >
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center gap-2">
          <BButton
            size="sm"
            variant="primary"
            :disabled="tableAreas.busy"
            @click.prevent="editArea(item)"
          >
            Edit
          </BButton>
          <BButton
            size="sm"
            variant="secondary"
            :disabled="tableAreas.busy"
            @click.prevent="() => $refs.areaClusters.manageAreaClusters(item)"
          >
            Clusters
          </BButton>
        </div>
      </template>
    </CustomTable>

    <BModal
      ref="modalArea"
      centered
      scrollable
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      aria-hidden="false"
      role="dialog"
      :title="MAreaTitle"
    >
      <ValidationObserver
        ref="formArea"
      >
        <form
          role="form"
          novalidate
          @submit.prevent
        >
          <BRow>

            <BCol
              cols="12"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="team"
                name="team"
                rules="required"
              >
                <BFormGroup
                  label="Team"
                  label-for="fTeam"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <VSelect
                    v-model="selected.team"
                    input-id="fTeam"
                    label="team_name"
                    append-to-body
                    placeholder="search team here"
                    :options="options.teams"
                    :disabled="fetching.teams"
                    :loading="fetching.teams"
                    :calculate-position="calculateDropPosition"
                    :class="{ 'is-invalid': errors.length > 0 }"
                    @search="searchTeams"
                  >
                    <template #no-options="">
                      no available team
                    </template>
                  </VSelect>
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
            >
              <ValidationProvider
                v-slot="{ errors }"
                name="area code"
                vid="area_code"
                rules="required|max:150"
              >
                <BFormGroup
                  label="Area Code"
                  label-for="fAreaCode"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fAreaCode"
                    v-model="formArea.area_code"
                    type="text"
                    maxlength="150"
                    autocomplete="off"
                    placeholder="enter area code here"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
            >
              <ValidationProvider
                v-slot="{ errors }"
                name="area name"
                vid="area_name"
                rules="required|max:150"
              >
                <BFormGroup
                  label="Area Name"
                  label-for="fAreaName"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fAreaName"
                    v-model="formArea.area_name"
                    type="text"
                    maxlength="150"
                    autocomplete="off"
                    placeholder="enter area name here"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              v-if="state.editing"
              cols="12"
            >
              <BFormGroup>
                <BFormCheckbox
                  id="fActive"
                  v-model="formArea.active"
                  switch
                  value="1"
                  name="active"
                  unchecked-value="0"
                  :disabled="state.busy"
                >
                  Is Active?
                </BFormCheckbox>
              </BFormGroup>
            </BCol>
          </BRow>
        </form>
      </ValidationObserver>

      <template #modal-footer="{cancel}">
        <BButton
          variant="primary"
          :disabled="state.busy"
          @click.prevent="validateArea"
        >
          {{ state.editing ? 'Update' : 'Create' }} Area
        </BButton>
        <BButton

          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          Discard
        </BButton>
      </template>
    </BModal>

    <AreaClusters
      ref="areaClusters"
    />
  </BContainer>
</template>

<script>
import { mapGetters } from 'vuex'
import { find } from 'lodash'

import AreaClusters from '@/views/admin/locations/areas/AreaClusters.vue'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import MTeams from '@/mixins/locations/teams'

import { SAdminArea } from '@/services/admin'

export default {
  name: 'VAdminLocationsAreas',

  components: {
    AreaClusters
  },

  mixins: [
    MxMisc,
    MxFormatter,
    MTeams
  ],

  middleware: [
    'auth',
    'admin'
  ],

  metaInfo: () => ({
    title: 'Areas'
  }),

  data () {
    return {
      state: {
        busy: false,
        editing: false
      },
      formArea: {
        id: '',
        team_id: '',
        area_code: '',
        area_name: '',
        active: 1
      },
      tableAreas: {
        busy: false,
        filter: {
          search: '',
          team: ''
        },
        filterOn: [],
        stacked: 'sm',
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 7, key: 'action', class: 'text-center w-md-125px' },
          { mobile: 1, key: 'team.team_name', label: 'Team', class: 'min-w-150px' },
          { mobile: 2, key: 'area_code', class: 'min-w-150px' },
          { mobile: 3, key: 'area_name', class: 'min-w-175px' },
          { mobile: 5, key: 'active', class: 'text-md-center w-md-75px', formatter: this.yesOrNo },
          { mobile: 6, key: 'updated_at', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: {
    ...mapGetters({
      currentUser: 'auth/user'
    }),

    MAreaTitle () {
      return this.state.editing ? 'Edit Area' : 'Create Area'
    }
  },

  watch: {
    'selected.team' (team) {
      this.formArea.team_id = team?.id || ''
    }
  },

  methods: {

    vTabAreaDetails () {
      if (this.$refs.formArea) {
        const formAreaErrors = this.$refs.formArea.errors
        return Object.keys(formAreaErrors).some(
          errorKey => formAreaErrors[errorKey]?.length > 0
        )
      }

      return false
    },

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableAreas.busy = true

      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search
      }

      return await SAdminArea.get(payload).then(
        ({ data }) => {
          this.tableAreas.totalRows = data.total_rows
          return data.items
        }
      ).catch(() => {
        this.tableAreas.totalRows = 0
        return []
      }).finally(() => {
        this.tableAreas.busy = false
      })
    },

    createArea () {
      this.state.editing = false

      this.formArea = Object.assign(this.formArea, {
        id: '',
        area_code: '',
        area_name: '',
        active: 1
      })

      this.getTeams(this)

      this.$refs.modalArea.show()
    },

    editArea (area) {
      this.state.editing = true

      this.formArea = Object.assign(this.formArea, {
        id: area.id,
        area_code: area.area_code,
        area_name: area.area_name,
        active: area.active
      })

      this.getTeams(this).finally(() => {
        this.selected.team = find(this.options.teams, {
          id: area?.team_id || ''
        }) || ''
      })

      this.$refs.modalArea.show()
    },

    editAreaClusters () {

    },

    updateProvinces (provinces) {
      this.formArea.provinces = provinces
    },

    async validateArea () {
      await this.$refs.formArea.validate().then(
        async validArea => {
          if (!validArea) {
            return this.swalInvalid()
          }

          this.swalConfirm({
            html: this.state.editing ? '<h6>Update this Area?</h6>' : '<h6>Create this Area?</h6>',
            confirmButtonText: this.state.editing ? 'Update' : 'Create',
            preConfirm: async () => {
              this.state.busy = true

              const createOrUpdate = async () => {
                return this.state.editing ? this.putArea() : this.postArea()
              }

              return createOrUpdate().catch(
                ({ message }) => {
                  this.$refs.formArea.setErrors(message.errors)
                }
              ).finally(() => {
                this.state.busy = false
              })
            }
          })
        }
      )
    },

    async postArea () {
      return await SAdminArea.post(this.formArea).then(
        ({ data: { message } }) => {
          this.swalSuccess(message).then(() => {
            this.$refs.modalArea.hide()
            this.$refs.tableAreas.refetch()
          })
        }
      )
    },

    async putArea () {
      return await SAdminArea.put(this.formArea).then(
        ({ data: { message, area } }) => {
          this.swalSuccess(message).then(() => {
            this.$refs.modalArea.hide()
            this.$refs.tableAreas.updateRow({
              id: area.id,
              team_id: area.team_id,
              team: area.team,
              area_code: area.area_code,
              area_name: area.area_name,
              active: area.active,
              updated_at: area.updated_at
            })
          })
        }
      )
    }
  }
}
</script>
